import { describeMutationError } from "@api/mutationErrors";
import { ensureFreshSession } from "@auth/refreshMiddleware";
import { createMicSource, type PcmSource } from "@transcription/pcmSource";
import { encodeWav } from "@transcription/wav";
import { decodeBase64Pcm16, pcmIsAudible, PlaybackQueue } from "@voice/audio";
import { createEchoPlayback } from "@voiceprint/echoPlayback";
import { InterviewSample, isSpeech } from "@voiceprint/interviewSample";
import { useUploadVoiceprintSample } from "@voiceprint/voiceprintQueries";
import { useCallback, useEffect, useRef, useState } from "react";

import { getConfig } from "../config";

// Leave time to upload before the backend interview's 300-second deadline.
const REQUEST_FINISH_MS = 225_000;
const FORCE_FINISH_MS = 255_000;
const CLOSING_WAIT_MS = 30_000;

type Status =
  | "idle"
  | "connecting"
  | "listening"
  | "finishing"
  | "uploading"
  | "ready"
  | "error";
interface Turn {
  role: "You" | "Brian";
  text: string;
}
interface Interview {
  key: string;
  source: PcmSource;
  context: AudioContext;
  playback: PlaybackQueue;
  echoPlayback: ReturnType<typeof createEchoPlayback>;
  sample: InterviewSample;
  socket: WebSocket | null;
  timer: ReturnType<typeof setInterval> | null;
  ready: boolean;
  captureStopped: boolean;
  finishingAt: number | null;
  lastUserSpeechAt: number;
  lastAudioAt: number;
  closingHeard: boolean;
  role: "You" | "Brian" | null;
  brianText: string;
}

function decimate(chunk: ArrayBuffer, factor: number): ArrayBuffer {
  const input = new Int16Array(chunk);
  const output = new Int16Array(Math.floor(input.length / factor));
  for (let i = 0; i < output.length; i++) {
    let sum = 0;
    for (let j = 0; j < factor; j++) sum += input[i * factor + j];
    output[i] = Math.round(sum / factor);
  }
  return output.buffer;
}

function release(interview: Interview): void {
  if (interview.timer !== null) clearInterval(interview.timer);
  interview.source.stop();
  interview.playback.flush();
  interview.echoPlayback.close();
  void interview.context.close().catch(() => {});
  const socket = interview.socket;
  if (socket) {
    socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null;
    socket.close();
  }
  interview.sample.clear();
}

export function useEnrollmentInterview() {
  const uploadSample = useUploadVoiceprintSample();
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [speechSeconds, setSpeechSeconds] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const current = useRef<Interview | null>(null);
  const pending = useRef<{ blob: Blob; key: string } | null>(null);
  const uploading = useRef(false);
  const cleanup = useCallback(() => {
    const interview = current.current;
    current.current = null;
    if (interview) release(interview);
    setSpeaking(false);
  }, []);

  useEffect(
    () => () => {
      const interview = current.current;
      current.current = null;
      if (interview) release(interview);
      pending.current = null;
    },
    [],
  );

  const upload = useCallback(async () => {
    const sample = pending.current;
    if (!sample || uploading.current) return;
    uploading.current = true;
    setStatus("uploading");
    setError(null);
    try {
      await uploadSample.mutateAsync({
        file: sample.blob,
        sittingKey: sample.key,
      });
      if (pending.current !== sample) return;
      pending.current = null;
      setStatus("ready");
    } catch (e) {
      if (pending.current !== sample) return;
      setStatus("error");
      setError(describeMutationError(e, "build your voice signature"));
    } finally {
      uploading.current = false;
      uploadSample.reset();
    }
  }, [uploadSample]);

  const finish = useCallback(
    (interview: Interview) => {
      if (current.current !== interview) return;
      const seconds = interview.sample.speechSeconds;
      const chunks = interview.sample.take();
      cleanup();
      setTurns([]);
      if (seconds < 1.5) {
        setStatus("error");
        setError(
          "We couldn't collect enough clear speech. Please try again in a quiet place.",
        );
        return;
      }
      const blob = encodeWav(chunks);
      pending.current = { blob, key: interview.key };
      void upload();
    },
    [cleanup, upload],
  );

  const requestFinish = useCallback((interview: Interview) => {
    if (interview.finishingAt !== null || !interview.ready) return;
    interview.finishingAt = performance.now();
    setStatus("finishing");
    interview.socket?.send(new TextEncoder().encode("finish"));
  }, []);

  const start = useCallback(async () => {
    if (current.current || uploading.current) return;
    pending.current = null;
    setTurns([]);
    setError(null);
    setSpeechSeconds(0);
    setStatus("connecting");
    const context = new AudioContext({ sampleRate: 24000 });
    const echoPlayback = createEchoPlayback(context);
    const interview: Interview = {
      key: crypto.randomUUID(),
      source: createMicSource({
        targetRate: 48000,
        contextSampleRate: 48000,
        requireEchoCancellation: true,
      }),
      context,
      playback: new PlaybackQueue({
        get currentTime() {
          return context.currentTime;
        },
        createBuffer: (...args) => context.createBuffer(...args),
        createBufferSource: () => context.createBufferSource(),
        destination: echoPlayback.destination,
      }),
      echoPlayback,
      sample: new InterviewSample(),
      socket: null,
      timer: null,
      ready: false,
      captureStopped: false,
      finishingAt: null,
      lastUserSpeechAt: -Infinity,
      lastAudioAt: -Infinity,
      closingHeard: false,
      role: null,
      brianText: "",
    };
    current.current = interview;
    const fail = (message: string) => {
      if (current.current !== interview) return;
      cleanup();
      setTurns([]);
      setStatus("error");
      setError(message);
    };
    try {
      await context.resume();
      await echoPlayback.start();
      if (current.current !== interview) return;
      const token = (await ensureFreshSession())?.accessToken;
      if (current.current !== interview) return;
      if (!token) throw new Error("Please sign in again.");
      await interview.source.start({
        whenReady: Promise.resolve(),
        emit: (chunk) => {
          if (current.current !== interview || !interview.ready) return;
          const socket = interview.socket;
          if (socket?.readyState !== WebSocket.OPEN) return;
          if (socket.bufferedAmount > 240000) {
            fail("The connection is too slow. Please try again.");
            return;
          }
          const now = performance.now();
          const userAudio = decimate(chunk, 3);
          interview.sample.observe(userAudio);
          if (isSpeech(userAudio)) interview.lastUserSpeechAt = now;
          const pcm = new Uint8Array(decimate(chunk, 2));
          const frame = new Uint8Array(pcm.length + 1);
          frame.set(pcm, 1);
          socket.send(frame);
          setSpeechSeconds(Math.floor(interview.sample.speechSeconds));
        },
      });
      if (current.current !== interview) {
        interview.source.stop();
        return;
      }
      const socket = new WebSocket(
        `${getConfig().wsUrl}/voiceprints/me/interview`,
        ["scribenote.auth.bearer.v1", token],
      );
      interview.socket = socket;
      const connectingAt = performance.now();
      socket.onopen = () =>
        socket.send(JSON.stringify({ type: "start", consent: true }));
      socket.onmessage = (event) => {
        if (current.current !== interview || typeof event.data !== "string")
          return;
        try {
          const frame = JSON.parse(event.data) as {
            type: string;
            payload?: string;
            text?: string;
            role?: string;
            message?: string;
          };
          if (frame.type === "ready") {
            interview.ready = true;
            setStatus("listening");
          } else if (frame.type === "audio" && frame.payload) {
            const audio = decodeBase64Pcm16(frame.payload);
            if (pcmIsAudible(audio)) {
              if (interview.role !== "Brian") {
                interview.role = "Brian";
                interview.brianText = "";
              }
              interview.lastAudioAt = performance.now();
            }
            interview.playback.enqueue(audio);
          } else if (
            frame.type === "transcript_delta" &&
            frame.text &&
            (frame.role === "You" || frame.role === "Brian")
          ) {
            const role = frame.role;
            if (role === "You") {
              if (interview.role !== "You") {
                interview.playback.flush();
                interview.role = "You";
              }
              interview.sample.recognizeSpeech();
            } else {
              if (interview.role !== "Brian") {
                interview.role = "Brian";
                interview.brianText = "";
              }
              interview.brianText += frame.text;
              if (
                interview.brianText
                  .toLowerCase()
                  .includes("this completes the voice enrollment exercise")
              ) {
                interview.closingHeard = true;
                interview.finishingAt ??= performance.now();
                setStatus("finishing");
              }
            }
            const text = frame.text;
            setTurns((previous) => {
              const last = previous.at(-1);
              return last?.role === role
                ? [
                    ...previous.slice(0, -1),
                    { role, text: (last.text + text).slice(-4000) },
                  ]
                : [...previous.slice(-19), { role, text: text.slice(-4000) }];
            });
          } else if (frame.type === "error")
            fail(frame.message ?? "Conversation interrupted.");
        } catch {
          fail("Conversation interrupted. Please try again.");
        }
      };
      socket.onerror = () =>
        fail("Couldn't connect to the enrollment conversation.");
      socket.onclose = () =>
        fail("The enrollment conversation disconnected. Please try again.");
      interview.timer = setInterval(() => {
        const now = performance.now();
        if (now - connectingAt >= FORCE_FINISH_MS) {
          finish(interview);
          return;
        }
        const talking = interview.playback.audible;
        if (
          interview.captureStopped &&
          interview.socket?.readyState === WebSocket.OPEN
        ) {
          interview.socket.send(new Uint8Array(4801));
        }
        setSpeaking(talking);
        if (
          !talking &&
          interview.sample.speechSeconds >= 45 &&
          now - interview.lastUserSpeechAt >= 1200
        ) {
          requestFinish(interview);
        }
        if (!interview.ready && now - connectingAt > 25000) {
          fail("The enrollment conversation couldn't start. Please try again.");
          return;
        }
        if (
          interview.closingHeard &&
          !interview.playback.hasPendingSpeech &&
          now - interview.lastAudioAt > 1500
        ) {
          finish(interview);
        } else if (
          interview.finishingAt !== null &&
          now - interview.finishingAt >= CLOSING_WAIT_MS
        ) {
          finish(interview);
        } else if (now - connectingAt >= REQUEST_FINISH_MS)
          requestFinish(interview);
      }, 100);
    } catch (e) {
      fail(e instanceof Error ? e.message : "Couldn't start your microphone.");
    }
  }, [cleanup, finish, requestFinish]);

  const stop = useCallback(() => {
    const interview = current.current;
    if (!interview) return;
    interview.source.stop();
    interview.captureStopped = true;
    interview.sample.finishTurn();
    requestFinish(interview);
  }, [requestFinish]);

  const cancel = useCallback(() => {
    cleanup();
    pending.current = null;
    setTurns([]);
    setStatus("idle");
    setError(null);
    setSpeechSeconds(0);
  }, [cleanup]);

  return {
    status,
    error,
    turns,
    speechSeconds,
    speaking,
    start,
    stop,
    cancel,
    retry: upload,
    canRetry: pending.current !== null,
  };
}
